import { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { PawPrint } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
import Button from '../../components/ui/Button';
import LoadingSpinner from '../../components/feedback/LoadingSpinner';
import { ROUTES } from '../../config/constants';

/**
 * Ícono de Google inline — evitamos depender de un asset externo
 * y mantenemos el logo oficial sin riesgo de mismatch de branding.
 */
const GoogleIcon = (props) => (
  <svg viewBox="0 0 24 24" {...props}>
    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC04"/>
    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
  </svg>
);

const LoginPage = () => {
  const { signIn, status, isAuthenticated, hasFamily } = useAuth();
  const [signingIn, setSigningIn] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const toast = useToast();

  /**
   * Si ya hay sesión activa, redirigimos al destino correcto.
   * Esto cubre el caso de un usuario autenticado que aterriza en /login
   * (back button, link compartido, etc.).
   */
  useEffect(() => {
    if (status !== 'authenticated') return;
    const from = location.state?.from?.pathname;
    if (hasFamily) {
      navigate(from || ROUTES.PETS, { replace: true });
    } else {
      navigate(ROUTES.ONBOARDING, { replace: true });
    }
  }, [status, isAuthenticated, hasFamily, navigate, location.state]);

  const handleSignIn = async () => {
    try {
      setSigningIn(true);
      await signIn();
      // La redirección ocurre en el useEffect tras el cambio de status.
    } catch (err) {
      toast.error(err.message || 'No se pudo iniciar sesión.');
    } finally {
      setSigningIn(false);
    }
  };

  if (status === 'initializing') {
    return (
      <div className="flex min-h-[100dvh] items-center justify-center bg-slate-50">
        <LoadingSpinner label="Verificando sesión..." />
      </div>
    );
  }

  return (
    <div className="flex min-h-[100dvh] flex-col bg-gradient-to-b from-brand-50 to-white">
      <main className="flex flex-1 flex-col items-center justify-center px-6 py-12">
        <div className="w-full max-w-sm">
          <div className="mb-8 flex flex-col items-center text-center">
            <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-2xl bg-brand-700 shadow-lg">
              <PawPrint className="h-8 w-8 text-white" aria-hidden="true" />
            </div>
            <h1 className="text-2xl font-bold text-slate-900">PetCare Family</h1>
            <p className="mt-2 text-sm text-slate-600">
              Coordiná el cuidado de tus mascotas con toda la familia.
            </p>
          </div>

          <div className="rounded-2xl bg-white p-6 shadow-sm ring-1 ring-slate-200/60">
            <h2 className="mb-1 text-lg font-semibold text-slate-900">Iniciar sesión</h2>
            <p className="mb-5 text-sm text-slate-500">
              Usá tu cuenta de Google para empezar.
            </p>

            <Button
              variant="secondary"
              size="lg"
              fullWidth
              isLoading={signingIn}
              onClick={handleSignIn}
              leftIcon={(props) => <GoogleIcon {...props} className="h-5 w-5" />}
            >
              Continuar con Google
            </Button>

            <p className="mt-5 text-center text-xs text-slate-400">
              Al continuar aceptás los términos del servicio.
            </p>
          </div>
        </div>
      </main>

      <footer className="px-6 pb-6 text-center">
        <p className="text-xs text-slate-400">
          © {new Date().getFullYear()} PetCare Family
        </p>
      </footer>
    </div>
  );
};

export default LoginPage;